import type { Dispatch, SetStateAction } from "react";
import type { PlatformRow } from "./salesChecklistOrgEditorTypes";

type Props = {
  loading: boolean;
  adsEnabled: boolean;
  setAdsEnabled: (v: boolean) => void;
  /** Gamification Phase 1: feed this org's checklist ad goal into the Org Activity verdict. */
  adsUseInOrgActivity: boolean;
  setAdsUseInOrgActivity: (v: boolean) => void;
  platformRows: PlatformRow[];
  setPlatformRows: Dispatch<SetStateAction<PlatformRow[]>>;
  topicKeywords: string;
  setTopicKeywords: (v: string) => void;
  managerNudgePersist: { title: string; body: string; sent_at: string } | null;
  nudgeTitle: string;
  setNudgeTitle: (v: string) => void;
  nudgeBody: string;
  setNudgeBody: (v: string) => void;
  broadcastBusy: boolean;
  onSendNudge: () => void;
  leadHygieneDays: string;
  setLeadHygieneDays: (v: string) => void;
  leadHygieneCadence: string;
  setLeadHygieneCadence: (v: string) => void;
  ackBlockingEnabled: boolean;
  setAckBlockingEnabled: (v: boolean) => void;
  floodGuardrailsEnabled: boolean;
  setFloodGuardrailsEnabled: (v: boolean) => void;
  leadDefaultFirstFollowUpEnabled: boolean;
  setLeadDefaultFirstFollowUpEnabled: (v: boolean) => void;
  leadDefaultHotAction: string;
  setLeadDefaultHotAction: (v: string) => void;
  leadDefaultWarmAction: string;
  setLeadDefaultWarmAction: (v: string) => void;
  leadDefaultColdAction: string;
  setLeadDefaultColdAction: (v: string) => void;
  leadDefaultDueHoursHot: string;
  setLeadDefaultDueHoursHot: (v: string) => void;
  leadDefaultDueHoursWarm: string;
  setLeadDefaultDueHoursWarm: (v: string) => void;
  leadDefaultDueHoursCold: string;
  setLeadDefaultDueHoursCold: (v: string) => void;
};

export function SalesChecklistSettingsSection(props: Props) {
  return (
    <>
      <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-zinc-900/30 px-4 py-4 sm:px-5 space-y-4">
        <label className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100"><input type="checkbox" checked={props.adsEnabled} onChange={(e) => props.setAdsEnabled(e.target.checked)} disabled={props.loading} />Include ad logging toward daily progress</label>
        <label className="ml-6 flex items-center gap-2 text-[11px] text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={props.adsUseInOrgActivity}
            onChange={(e) => props.setAdsUseInOrgActivity(e.target.checked)}
            disabled={props.loading || !props.adsEnabled}
          />
          Use this goal in Ads Assistant Org Activity stats
        </label>
        {props.adsEnabled ? (
          <div className="space-y-4 pl-0 sm:pl-1">
            <div>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Platforms &amp; slots per day</span>
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:underline"
                  disabled={props.loading}
                  onClick={() =>
                    props.setPlatformRows((rows) => [
                      ...rows,
                      { rowId: crypto.randomUUID(), key: "new_platform", label: "New platform", daily_slots: 1 },
                    ])
                  }
                >
                  + Add platform
                </button>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">Default labels: <strong>Facebook Business Page</strong> and <strong>Google Business</strong>. Each row is a separate cap; we only have automated page pulls for facebook / google today — other keys are tracked for the roadmap.</p>
              <div className="space-y-3">
                {props.platformRows.map((p, idx) => (
                  <div
                    key={p.rowId ?? `platform-row-${idx}`}
                    className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-zinc-950/50 px-3 py-3 sm:px-4"
                  >
                    <label className="text-[11px] text-gray-600 dark:text-gray-400 min-w-[5rem] flex-1">Key<input className="mt-0.5 w-full text-xs font-mono border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-zinc-900" value={p.key} onChange={(e) => props.setPlatformRows((rows) => rows.map((r, i) => (i === idx ? { ...r, key: e.target.value } : r)))} disabled={props.loading} /></label>
                    <label className="text-[11px] text-gray-600 dark:text-gray-400 flex-[2] min-w-[10rem]">Display label<input className="mt-0.5 w-full text-xs border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-zinc-900" value={p.label} onChange={(e) => props.setPlatformRows((rows) => rows.map((r, i) => (i === idx ? { ...r, label: e.target.value } : r)))} disabled={props.loading} /></label>
                    <label className="text-[11px] text-gray-600 dark:text-gray-400 w-24">Slots<input type="number" min={1} max={20} className="mt-0.5 w-full text-xs border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-zinc-900" value={p.daily_slots} onChange={(e) => props.setPlatformRows((rows) => rows.map((r, i) => i === idx ? { ...r, daily_slots: Math.min(20, Math.max(1, parseInt(e.target.value, 10) || 1)) } : r))} disabled={props.loading} /></label>
                    <button type="button" className="text-xs text-red-600 hover:underline mb-0.5" disabled={props.loading} onClick={() => { const label = p.label.trim() || p.key || "this platform"; if (!window.confirm(`Remove “${label}” from Platforms & slots per day?\n\nThis change applies to your entire organization: everyone’s Leads daily checklist uses this template, including ad slot targets and manual-ingest platform options. Save is still required to persist.`)) return; props.setPlatformRows((rows) => rows.filter((_, i) => i !== idx)); }}>Remove</button>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Caption keywords (optional)</label>
              <input className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-zinc-900" placeholder="Leave blank to count all posts on the platforms above" value={props.topicKeywords} onChange={(e) => props.setTopicKeywords(e.target.value)} disabled={props.loading} />
              <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug"><strong>Blank</strong> = every post caption qualifies (still per-platform slot caps). <strong>With keywords</strong> = a post counts only if its caption matches <em>any</em> keyword (comma-separated, OR logic).</p>
            </div>
          </div>
        ) : null}
      </div>

      <div className="rounded-xl border border-blue-200/80 dark:border-blue-800/60 bg-blue-50/40 dark:bg-blue-950/25 px-4 py-4 sm:px-5 space-y-3">
        <div><span className="text-xs font-semibold text-gray-800 dark:text-gray-100">Org nudge (workplace notifications + checklist)</span><p className="text-[11px] text-gray-600 dark:text-gray-400 mt-1 leading-snug">Sends the same message to <strong>every active member</strong> of this org via the header bell (<span className="font-mono text-[10px]">ops_in_app_notifications</span>) and pins the text below on the Leads <strong>Daily checklist</strong> until you send a newer nudge or clear the org template.</p></div>
        <label className="text-[11px] text-gray-700 dark:text-gray-300 block">Title<input className="mt-0.5 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-zinc-900" value={props.nudgeTitle} onChange={(e) => props.setNudgeTitle(e.target.value)} placeholder="e.g. Need deposit receipts by Friday" disabled={props.loading || props.broadcastBusy} maxLength={500} /></label>
        <label className="text-[11px] text-gray-700 dark:text-gray-300 block">Message (optional)<textarea className="mt-0.5 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-zinc-900 min-h-[4rem]" value={props.nudgeBody} onChange={(e) => props.setNudgeBody(e.target.value)} placeholder="Extra detail for the notification and checklist banner." disabled={props.loading || props.broadcastBusy} maxLength={8000} /></label>
        <div className="flex flex-wrap items-center gap-2"><button type="button" disabled={props.loading || props.broadcastBusy || !props.nudgeTitle.trim()} onClick={props.onSendNudge} className="rounded-lg bg-blue-600 text-white px-3 py-2 text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 dark:bg-blue-700 dark:hover:bg-blue-600">{props.broadcastBusy ? "Sending…" : "Send to team & update checklist"}</button>{props.managerNudgePersist ? <span className="text-[10px] text-gray-500 dark:text-gray-400">Last sent: {new Date(props.managerNudgePersist.sent_at).toLocaleString()}</span> : null}</div>
      </div>

      <div className="rounded-xl border border-emerald-200/80 dark:border-emerald-800/50 bg-emerald-50/35 dark:bg-emerald-950/20 px-4 py-4 sm:px-5 space-y-3">
        <div>
          <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">All new leads — first follow-up event (trainer+)</span>
          <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-1 leading-snug">
            When enabled, each new lead gets <strong>one</strong> open follow-up row after transcript extraction, using the text and timing below (by interest level). When off, no automatic first follow-up is created — reps add events from the Customer Calendar instead.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
          <input
            type="checkbox"
            checked={props.leadDefaultFirstFollowUpEnabled}
            onChange={(e) => props.setLeadDefaultFirstFollowUpEnabled(e.target.checked)}
            disabled={props.loading}
          />
          Create default first follow-up on new lead extraction
        </label>
        <div className="grid gap-2 sm:grid-cols-2 text-[11px] text-gray-700 dark:text-gray-300">
          <label className="block sm:col-span-2">
            Hot — next action
            <input
              className="mt-0.5 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-zinc-900"
              value={props.leadDefaultHotAction}
              onChange={(e) => props.setLeadDefaultHotAction(e.target.value)}
              disabled={props.loading || !props.leadDefaultFirstFollowUpEnabled}
            />
          </label>
          <label>
            Hot — due in (hours)
            <input
              type="number"
              min={1}
              max={720}
              className="mt-0.5 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-zinc-900"
              value={props.leadDefaultDueHoursHot}
              onChange={(e) => props.setLeadDefaultDueHoursHot(e.target.value)}
              disabled={props.loading || !props.leadDefaultFirstFollowUpEnabled}
            />
          </label>
          <label className="block sm:col-span-2">
            Warm / unknown — next action
            <input
              className="mt-0.5 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-zinc-900"
              value={props.leadDefaultWarmAction}
              onChange={(e) => props.setLeadDefaultWarmAction(e.target.value)}
              disabled={props.loading || !props.leadDefaultFirstFollowUpEnabled}
            />
          </label>
          <label>
            Warm — due in (hours)
            <input
              type="number"
              min={1}
              max={720}
              className="mt-0.5 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-zinc-900"
              value={props.leadDefaultDueHoursWarm}
              onChange={(e) => props.setLeadDefaultDueHoursWarm(e.target.value)}
              disabled={props.loading || !props.leadDefaultFirstFollowUpEnabled}
            />
          </label>
          <label className="block sm:col-span-2">
            Cold — next action
            <input
              className="mt-0.5 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-zinc-900"
              value={props.leadDefaultColdAction}
              onChange={(e) => props.setLeadDefaultColdAction(e.target.value)}
              disabled={props.loading || !props.leadDefaultFirstFollowUpEnabled}
            />
          </label>
          <label>
            Cold — due in (hours)
            <input
              type="number"
              min={1}
              max={720}
              className="mt-0.5 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-zinc-900"
              value={props.leadDefaultDueHoursCold}
              onChange={(e) => props.setLeadDefaultDueHoursCold(e.target.value)}
              disabled={props.loading || !props.leadDefaultFirstFollowUpEnabled}
            />
          </label>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-zinc-900/30 px-4 py-4 sm:px-5 space-y-2">
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Lead follow-up hygiene (optional)</span>
        <label className="text-[11px] text-gray-600 dark:text-gray-400 block">Consider leads stale after (days)<input type="number" min={1} max={365} className="mt-0.5 w-full max-w-[12rem] text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-zinc-900" placeholder="Leave blank to disable" value={props.leadHygieneDays} onChange={(e) => props.setLeadHygieneDays(e.target.value)} disabled={props.loading} /></label>
        <label className="text-[11px] text-gray-600 dark:text-gray-400 block">Stale-lead notification cadence (days, comma-separated)<input className="mt-0.5 w-full max-w-[20rem] text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-zinc-900" placeholder="Default: 3, 7, 14" value={props.leadHygieneCadence} onChange={(e) => props.setLeadHygieneCadence(e.target.value)} disabled={props.loading || !props.leadHygieneDays.trim()} /></label>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
          When stale threshold is set, assigned reps receive per-lead hygiene nudges at each cadence tier (default{" "}
          <strong>3 / 7 / 14</strong> days without follow-up). Reps can <strong>dismiss</strong> or{" "}
          <strong>resolve</strong> with a note; managers can <strong>reassign</strong>. Resolution is logged on the lead
          timeline. Nudges do not block daily checklist completion.
        </p>
        <label className="inline-flex items-center gap-2 text-[11px] text-gray-700 dark:text-gray-300"><input type="checkbox" checked={props.ackBlockingEnabled} onChange={(e) => props.setAckBlockingEnabled(e.target.checked)} disabled={props.loading} />Block daily completion when required acknowledgements are unresolved</label>
        <label className="inline-flex items-center gap-2 text-[11px] text-gray-700 dark:text-gray-300"><input type="checkbox" checked={props.floodGuardrailsEnabled} onChange={(e) => props.setFloodGuardrailsEnabled(e.target.checked)} disabled={props.loading} />Cap high-volume system notifications per user per day (flood guardrails)</label>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
          When enabled, sales reps cannot close out the Leads daily checklist while workplace messages marked{" "}
          <strong>Require acknowledgement</strong> are still open in the header inbox.{" "}
          <strong>Personal</strong> user reminders and <strong>org nudges</strong> (manager broadcast, hygiene hints)
          are dismissible and do <strong>not</strong> block completion. Optional ack deadlines release the block after
          they pass. Lead-scoped reminders surface on the lead editor and in the header bell.
        </p>
      </div>
    </>
  );
}

